import { LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { getInitials } from '../../lib/utils/format';
import { CompanySwitcher } from './CompanySwitcher';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface MobileHeaderProps {
  /**
   * Current authenticated user
   */
  user: SupabaseUser | null;
  /**
   * Sign out handler
   */
  onSignOut: () => void;
}

/**
 * MobileHeader - Sticky header for mobile devices
 * 
 * Features:
 * - Company switcher (left)
 * - User dropdown menu (right)
 * - Sticky positioning
 * - Backdrop blur effect
 * 
 * @example
 * ```tsx
 * <MobileHeader
 *   user={currentUser}
 *   onSignOut={handleSignOut}
 * />
 * ```
 */
export function MobileHeader({ user, onSignOut }: MobileHeaderProps) {
  return (
    <header className="sticky top-0 z-50 flex h-16 items-center justify-between border-b bg-background/95 backdrop-blur px-4 md:hidden shadow-sm">
      {/* Company Switcher */}
      <div className="flex-1 min-w-0 mr-3">
        <CompanySwitcher />
      </div>

      {/* User Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="shrink-0 active:scale-95 transition-transform"
            aria-label="Menu do usuário"
          >
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary text-primary-foreground">
                {user?.email ? getInitials(user.email) : 'U'}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 mr-2">
          <DropdownMenuLabel>
            <p>
              {user?.email || 'Usuário'}
            </p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onSignOut}
            className="flex items-center gap-3 cursor-pointer text-destructive focus:text-destructive py-3"
          >
            <LogOut className="h-5 w-5" />
            <span>Sair</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
